import * as React from 'react';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../dialog.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LocationPickerLocation {
  id: string;
  name: string;
  status: 'active' | 'inactive';
}

/**
 * Visible copy. `@jq4race/ui` has no i18n, so callers pass translated strings;
 * every field defaults to English.
 */
export interface LocationPickerLabels {
  title: string;
  /** Description shown when there is at least one active location. */
  description: string;
  /** Description shown when the account has no active locations. */
  emptyDescription: string;
  /** Body message shown when there are no active locations. */
  noLocations: string;
  /** Hint shown under the list when the picker is dismissible. */
  changeAnytime: string;
}

export const DEFAULT_LOCATION_PICKER_LABELS: LocationPickerLabels = {
  title: 'Select location',
  description: 'Select the location you will be operating in.',
  emptyDescription: 'Your account has no active locations assigned.',
  noLocations: 'No locations assigned. Contact your Administrator.',
  changeAnytime: 'You can change the active location at any time.',
};

export interface LocationPickerProps {
  /** Full location list (may include inactive — they are filtered out internally). */
  locations: LocationPickerLocation[];
  /** Currently selected location id, if any. */
  selectedId?: string;
  /** Called with the chosen location id when the user selects one. */
  onSelect: (id: string) => void;
  /**
   * When true, the picker cannot be dismissed without selecting a location.
   * The close button is hidden and backdrop clicks are suppressed.
   */
  forceSelect?: boolean;
  /**
   * Called when the user dismisses the picker (only possible when
   * `forceSelect` is false/undefined).
   */
  onDismiss?: () => void;
  /** Translated copy; any omitted field falls back to its English default. */
  labels?: Partial<LocationPickerLabels>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * LocationPicker — modal-style selector for operational location.
 *
 * Consumed by:
 * - `apps/admin/` AppShell for `supervisor` role
 * - `apps/pwa/` authenticated boot gate
 *
 * Persistence: The CALLER is responsible for writing the selected id to
 * `localStorage.setItem('jq4race:active_location_id', id)`. This component
 * only calls `onSelect(id)`.
 */
export function LocationPicker({
  locations,
  selectedId,
  onSelect,
  forceSelect = false,
  onDismiss,
  labels: labelOverrides,
}: LocationPickerProps) {
  const labels = { ...DEFAULT_LOCATION_PICKER_LABELS, ...labelOverrides };
  // Defense-in-depth: filter inactive entries even if the backend returns them.
  const activeLocations = locations.filter((l) => l.status === 'active');

  const [open, setOpen] = React.useState(true);

  function handleSelect(id: string) {
    onSelect(id);
    setOpen(false);
  }

  function handleOpenChange(next: boolean) {
    if (forceSelect) return; // Cannot dismiss
    if (!next) {
      setOpen(false);
      onDismiss?.();
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        // Remove default close button when forceSelect is active.
        className="max-w-md"
        // Prevent closing on outside click when forced.
        onInteractOutside={forceSelect ? (e) => e.preventDefault() : undefined}
        // Suppress escape key when forced.
        onEscapeKeyDown={forceSelect ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle className="text-jq-fg-1">{labels.title}</DialogTitle>
          <DialogDescription>
            {activeLocations.length === 0 ? labels.emptyDescription : labels.description}
          </DialogDescription>
        </DialogHeader>

        {activeLocations.length === 0 ? (
          <ZeroLocationsMessage message={labels.noLocations} />
        ) : (
          <LocationList
            locations={activeLocations}
            selectedId={selectedId}
            onSelect={handleSelect}
            forceSelect={forceSelect}
            changeAnytimeLabel={labels.changeAnytime}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ZeroLocationsMessage({ message }: { message: string }) {
  return (
    <div className="mt-4 rounded-sm border border-jq-border bg-jq-bg-muted/40 px-4 py-6 text-center">
      <p className="text-[13px] text-jq-fg-2">{message}</p>
    </div>
  );
}

interface LocationListProps {
  locations: LocationPickerLocation[];
  selectedId?: string;
  onSelect: (id: string) => void;
  forceSelect: boolean;
  changeAnytimeLabel: string;
}

function LocationList({
  locations,
  selectedId,
  onSelect,
  forceSelect,
  changeAnytimeLabel,
}: LocationListProps) {
  return (
    <div className="mt-4 space-y-2">
      {locations.map((loc) => {
        const isSelected = loc.id === selectedId;
        return (
          <button
            key={loc.id}
            type="button"
            onClick={() => onSelect(loc.id)}
            aria-pressed={isSelected}
            className={[
              'flex w-full items-center gap-3 rounded-md border px-4 py-3 text-left text-[14px] transition',
              isSelected
                ? 'border-jq-action-accent bg-jq-action-accent/10 font-medium text-jq-fg-1'
                : 'border-jq-border bg-jq-action-secondary/40 text-jq-fg-1 hover:border-jq-action-accent/50 hover:bg-jq-action-secondary/70',
            ].join(' ')}
          >
            <span
              className={[
                'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                isSelected ? 'border-jq-action-accent bg-jq-action-accent' : 'border-jq-fg-2/50',
              ].join(' ')}
            >
              {isSelected && (
                <span className="h-1.5 w-1.5 rounded-full bg-jq-action-accent-fg" aria-hidden />
              )}
            </span>
            <span className="flex-1">{loc.name}</span>
          </button>
        );
      })}

      {!forceSelect && (
        <p className="pt-1 text-center text-[12px] text-jq-fg-2">{changeAnytimeLabel}</p>
      )}
    </div>
  );
}
